// ArbScan calculation engine.
//
// Pure, framework-agnostic betting math shared by all three calculators
// (Arbitrage, Hedge, 3-Way). No React, no DOM, no side effects — every
// function takes plain inputs and returns plain data so the UI layer can
// format numbers and render dashes for missing/invalid values.
//
// All odds INPUTS are American format. Internally everything converts to
// decimal odds for the math, per the product spec.

// ---------------------------------------------------------------------------
// Odds conversion + parsing
// ---------------------------------------------------------------------------

/**
 * Convert American odds to decimal odds.
 *   +150 -> 2.5     (american / 100) + 1
 *   -130 -> 1.769…  (100 / |american|) + 1
 * `american` must be a valid American line (|value| >= 100). 0 is invalid.
 */
export function americanToDecimal(american: number): number {
  if (american > 0) return american / 100 + 1;
  return 100 / Math.abs(american) + 1;
}

/** Convert decimal odds back to an American string (e.g. "+150", "-130"). */
export function decimalToAmerican(decimal: number): string {
  if (decimal >= 2) return `+${Math.round((decimal - 1) * 100)}`;
  return `${Math.round(-100 / (decimal - 1))}`;
}

/**
 * Parse a raw American-odds field (string from an input, or number) into a
 * finite American number, or null when empty/invalid. Valid American lines
 * have a magnitude of at least 100. Returns null for "", "+", "-", "abc", 0,
 * and sub-100 magnitudes so the UI can show a dash instead of an error.
 */
export function parseAmerican(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === "number" ? value : parseFloat(String(value).trim());
  if (!Number.isFinite(n)) return null;
  if (Math.abs(n) < 100) return null;
  return n;
}

/** Parse + convert in one step: American field -> decimal odds, or null. */
export function americanFieldToDecimal(
  value: string | number | null | undefined
): number | null {
  const american = parseAmerican(value);
  if (american === null) return null;
  return americanToDecimal(american);
}

/** Parse a positive money/stake field. Returns null for empty/invalid/<=0. */
export function parsePositive(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === "number" ? value : parseFloat(String(value).trim());
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

// ---------------------------------------------------------------------------
// Core multi-leg arbitrage engine (powers both 2-leg and 3-way)
// ---------------------------------------------------------------------------

export interface LegResult {
  /** Decimal odds for this leg, or null if the leg's odds were invalid. */
  decimal: number | null;
  /** Stake placed on this leg. */
  stake: number | null;
  /** Return if this leg wins = stake * decimal. */
  payout: number | null;
}

export interface MultiLegResult {
  legs: LegResult[];
  /** Sum of 1/decimal across legs (the "book total"). < 1 means an arb exists. */
  bookTotal: number | null;
  /** Whether a guaranteed-profit arbitrage exists: bookTotal < 1. */
  isArb: boolean;
  /** Arbitrage margin as a percentage: (1 - bookTotal) * 100. Can be negative. */
  arbPct: number | null;
  totalStake: number | null;
  /** Worst-case return across all legs = min(payout). The guaranteed payout. */
  guaranteedPayout: number | null;
  /** guaranteedPayout - totalStake. Negative when there is no arb (a loss). */
  profit: number | null;
  /** profit / totalStake * 100. */
  profitPct: number | null;
  /** True when stakes were rounded to whole dollars. */
  roundedToDollar: boolean;
}

export type StakeMode =
  | { type: "total"; totalStake: number | null }
  | { type: "anchor"; index: number; stake: number | null };

interface MultiLegInput {
  /** Decimal odds per leg; null entries mark invalid/missing legs. */
  decimals: (number | null)[];
  mode: StakeMode;
  roundToDollar?: boolean;
}

/**
 * The shared arbitrage solver. Given decimal odds per leg and a stake mode,
 * it sizes every leg so each outcome returns the same amount (a balanced
 * book), then reports profit/loss. Works for any number of legs.
 *
 * Stake sizing:
 *  - "total": split a total stake S proportionally to 1/decimal so every
 *    leg pays back the same amount.  stake_i = S * (1/dec_i) / Σ(1/dec_j)
 *  - "anchor": fix one leg's stake, target its balanced return
 *    R = anchorStake * dec_anchor, then stake_i = R / dec_i for every leg.
 *
 * When roundToDollar is set, stakes are rounded to whole dollars and all
 * downstream figures (payouts, profit) are recomputed from the rounded
 * stakes — so the displayed numbers always reflect what is actually bet.
 */
export function solveMultiLeg(input: MultiLegInput): MultiLegResult {
  const { decimals, mode, roundToDollar = false } = input;

  const allValid = decimals.length > 0 && decimals.every((d): d is number => d !== null && d > 0);

  // Book total / arb detection only needs valid odds — independent of stakes.
  const bookTotal = allValid
    ? (decimals as number[]).reduce((sum, d) => sum + 1 / d, 0)
    : null;
  const isArb = bookTotal !== null && bookTotal < 1;
  const arbPct = bookTotal !== null ? (1 - bookTotal) * 100 : null;

  const emptyLegs: LegResult[] = decimals.map((d) => ({
    decimal: d,
    stake: null,
    payout: null,
  }));

  const fail = (): MultiLegResult => ({
    legs: emptyLegs,
    bookTotal,
    isArb,
    arbPct,
    totalStake: null,
    guaranteedPayout: null,
    profit: null,
    profitPct: null,
    roundedToDollar: roundToDollar,
  });

  if (!allValid) return fail();
  const decs = decimals as number[];

  // Determine raw stakes per the chosen mode.
  let stakes: number[];
  if (mode.type === "total") {
    const S = mode.totalStake;
    if (S === null || S <= 0) return fail();
    const denom = decs.reduce((sum, d) => sum + 1 / d, 0);
    stakes = decs.map((d) => (S * (1 / d)) / denom);
  } else {
    const { index, stake } = mode;
    if (stake === null || stake <= 0 || index < 0 || index >= decs.length) return fail();
    const targetReturn = stake * decs[index];
    stakes = decs.map((d) => targetReturn / d);
  }

  if (roundToDollar) {
    stakes = stakes.map((s) => Math.round(s));
  }

  const legs: LegResult[] = decs.map((d, i) => ({
    decimal: d,
    stake: stakes[i],
    payout: stakes[i] * d,
  }));

  const totalStake = stakes.reduce((sum, s) => sum + s, 0);
  const guaranteedPayout = Math.min(...legs.map((l) => l.payout as number));
  const profit = guaranteedPayout - totalStake;
  const profitPct = totalStake > 0 ? (profit / totalStake) * 100 : null;

  return {
    legs,
    bookTotal,
    isArb,
    arbPct,
    totalStake,
    guaranteedPayout,
    profit,
    profitPct,
    roundedToDollar: roundToDollar,
  };
}

// ---------------------------------------------------------------------------
// Tab 1 — Two-leg arbitrage calculator
// ---------------------------------------------------------------------------

export type ArbStakeMode = "anchorA" | "anchorB" | "total";

export interface ArbInput {
  oddsA: string | number | null | undefined;
  oddsB: string | number | null | undefined;
  mode: ArbStakeMode;
  /** Used when mode === "anchorA". */
  stakeA?: string | number | null;
  /** Used when mode === "anchorB". */
  stakeB?: string | number | null;
  /** Used when mode === "total". */
  totalStake?: string | number | null;
  roundToDollar?: boolean;
}

export interface ArbResult {
  legA: LegResult;
  legB: LegResult;
  bookTotal: number | null;
  isArb: boolean;
  arbPct: number | null;
  totalStake: number | null;
  guaranteedPayout: number | null;
  profit: number | null;
  profitPct: number | null;
  roundedToDollar: boolean;
}

/** Two-leg arbitrage. Anchor either book, or split a total stake optimally. */
export function calcArb(input: ArbInput): ArbResult {
  const decA = americanFieldToDecimal(input.oddsA);
  const decB = americanFieldToDecimal(input.oddsB);

  let mode: StakeMode;
  if (input.mode === "total") {
    mode = { type: "total", totalStake: parsePositive(input.totalStake) };
  } else if (input.mode === "anchorA") {
    mode = { type: "anchor", index: 0, stake: parsePositive(input.stakeA) };
  } else {
    mode = { type: "anchor", index: 1, stake: parsePositive(input.stakeB) };
  }

  const r = solveMultiLeg({ decimals: [decA, decB], mode, roundToDollar: input.roundToDollar });

  return {
    legA: r.legs[0],
    legB: r.legs[1],
    bookTotal: r.bookTotal,
    isArb: r.isArb,
    arbPct: r.arbPct,
    totalStake: r.totalStake,
    guaranteedPayout: r.guaranteedPayout,
    profit: r.profit,
    profitPct: r.profitPct,
    roundedToDollar: r.roundedToDollar,
  };
}

// ---------------------------------------------------------------------------
// Tab 3 — Three-way arbitrage calculator
// ---------------------------------------------------------------------------

export interface ThreeWayInput {
  oddsA: string | number | null | undefined;
  oddsB: string | number | null | undefined;
  oddsC: string | number | null | undefined;
  /** "total" splits a total stake; "anchor" fixes one leg (anchorIndex). */
  mode: "total" | "anchor";
  totalStake?: string | number | null;
  /** 0, 1 or 2 — which leg is anchored when mode === "anchor". */
  anchorIndex?: number;
  /** The fixed stake for the anchored leg. */
  anchorStake?: string | number | null;
  roundToDollar?: boolean;
}

export interface ThreeWayResult {
  legs: [LegResult, LegResult, LegResult];
  bookTotal: number | null;
  isArb: boolean;
  arbPct: number | null;
  totalStake: number | null;
  guaranteedPayout: number | null;
  profit: number | null;
  profitPct: number | null;
  roundedToDollar: boolean;
}

/** Three-leg arbitrage for Home/Draw/Away or any three-outcome market. */
export function calcThreeWay(input: ThreeWayInput): ThreeWayResult {
  const decimals = [
    americanFieldToDecimal(input.oddsA),
    americanFieldToDecimal(input.oddsB),
    americanFieldToDecimal(input.oddsC),
  ];

  const mode: StakeMode =
    input.mode === "total"
      ? { type: "total", totalStake: parsePositive(input.totalStake) }
      : { type: "anchor", index: input.anchorIndex ?? 0, stake: parsePositive(input.anchorStake) };

  const r = solveMultiLeg({ decimals, mode, roundToDollar: input.roundToDollar });

  return {
    legs: [r.legs[0], r.legs[1], r.legs[2]],
    bookTotal: r.bookTotal,
    isArb: r.isArb,
    arbPct: r.arbPct,
    totalStake: r.totalStake,
    guaranteedPayout: r.guaranteedPayout,
    profit: r.profit,
    profitPct: r.profitPct,
    roundedToDollar: r.roundedToDollar,
  };
}

// ---------------------------------------------------------------------------
// Tab 2 — Hedge calculator
// ---------------------------------------------------------------------------

export interface HedgeInput {
  /** Odds of the bet you already placed (American). */
  originalOdds: string | number | null | undefined;
  /** Amount you already staked on the original bet ($). */
  originalStake: string | number | null | undefined;
  /** Odds available on the opposite side now (American). */
  hedgeOdds: string | number | null | undefined;
  /** Round the hedge stake to the nearest dollar and recompute outcomes. */
  roundToDollar?: boolean;
}

export interface HedgeResult {
  originalDecimal: number | null;
  hedgeDecimal: number | null;
  /** Stake to place on the hedge to balance the two outcomes. */
  hedgeStake: number | null;
  /** Net profit if the original bet wins (hedge loses). */
  profitIfOriginalWins: number | null;
  /** Net profit if the hedge wins (original loses). */
  profitIfHedgeWins: number | null;
  /** The locked-in profit you are guaranteed = min of the two outcomes. */
  guaranteedProfit: number | null;
  /** True when the guaranteed profit is positive. */
  isProfit: boolean;
  roundedToDollar: boolean;
}

/**
 * Hedge an existing bet. Sizes the hedge stake so both outcomes return the
 * same, then reports the guaranteed locked profit (the hero figure in the UI).
 *
 *   hedgeStake          = originalStake * originalDec / hedgeDec
 *   profitIfOriginalWins= originalStake * originalDec - originalStake - hedgeStake
 *   profitIfHedgeWins   = hedgeStake   * hedgeDec    - originalStake - hedgeStake
 *   guaranteedProfit    = min(profitIfOriginalWins, profitIfHedgeWins)
 */
export function calcHedge(input: HedgeInput): HedgeResult {
  const roundToDollar = input.roundToDollar ?? false;
  const originalDecimal = americanFieldToDecimal(input.originalOdds);
  const hedgeDecimal = americanFieldToDecimal(input.hedgeOdds);
  const originalStake = parsePositive(input.originalStake);

  const empty: HedgeResult = {
    originalDecimal,
    hedgeDecimal,
    hedgeStake: null,
    profitIfOriginalWins: null,
    profitIfHedgeWins: null,
    guaranteedProfit: null,
    isProfit: false,
    roundedToDollar: roundToDollar,
  };

  if (originalDecimal === null || hedgeDecimal === null || originalStake === null) {
    return empty;
  }

  let hedgeStake = (originalStake * originalDecimal) / hedgeDecimal;
  if (roundToDollar) hedgeStake = Math.round(hedgeStake);

  const profitIfOriginalWins = originalStake * originalDecimal - originalStake - hedgeStake;
  const profitIfHedgeWins = hedgeStake * hedgeDecimal - originalStake - hedgeStake;
  const guaranteedProfit = Math.min(profitIfOriginalWins, profitIfHedgeWins);

  return {
    originalDecimal,
    hedgeDecimal,
    hedgeStake,
    profitIfOriginalWins,
    profitIfHedgeWins,
    guaranteedProfit,
    isProfit: guaranteedProfit > 0,
    roundedToDollar: roundToDollar,
  };
}

// ---------------------------------------------------------------------------
// Display helpers (optional, pure) — the UI may use these or its own.
// ---------------------------------------------------------------------------

/** Format a number as USD, or a dash when null/NaN. e.g. 1234.5 -> "$1,234.50". */
export function formatMoney(value: number | null | undefined, dash = "—"): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return dash;
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  return `${sign}$${abs.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Format a percentage with two decimals, or a dash when null. e.g. "2.41%". */
export function formatPct(value: number | null | undefined, dash = "—"): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return dash;
  return `${value.toFixed(2)}%`;
}
